import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A grid transformer yard: a large generator step-up transformer in a bunded
// compound, radiator banks down its flanks, tall HV bushings reaching to a steel
// gantry busbar, with a concrete blast wall behind. The high-power node where a
// plant meets the transmission grid.
const TANK = "#8a9197";
const TANK_DK = "#767d83";
const FIN = "#71787e";
const INSUL = "#9aa6ad";
const BUS = "#b87333";
const STEEL = "#878e96";
const WALL = "#b0a89a";
const BUND = "#7c766c";

export const transformerYard = defineModel({
  id: "transformer-yard",
  batchId: "electric",
  name: "Transformer yard",
  tags: ["building", "electric", "power", "grid", "transformer", "substation"],
  summary: "A large step-up transformer with gantry busbar and blast wall.",
  footprint: 1.0,
  parts(): Part[] {
    const parts: Part[] = [
      { geometry: new THREE.BoxGeometry(1.9, 0.1, 1.4), color: "#6f6f74", position: [0, 0.05, 0] },
      // Oil bund kerb.
      { geometry: new THREE.BoxGeometry(1.3, 0.08, 1.0), color: BUND, position: [-0.1, 0.12, 0.1] },
      // Concrete blast wall behind.
      { geometry: new THREE.BoxGeometry(1.5, 1.0, 0.1), color: WALL, position: [-0.1, 0.6, -0.55] },
      { geometry: new THREE.BoxGeometry(1.54, 0.08, 0.14), color: TANK_DK, position: [-0.1, 1.08, -0.55] },
      // Main tank.
      { geometry: new THREE.BoxGeometry(0.7, 0.62, 0.56), color: TANK, position: [-0.1, 0.45, 0.1] },
      { geometry: new THREE.BoxGeometry(0.74, 0.06, 0.6), color: TANK_DK, position: [-0.1, 0.79, 0.1] },
      // Radiator fin banks on both flanks.
      { geometry: new THREE.BoxGeometry(0.1, 0.5, 0.46), color: FIN, position: [-0.5, 0.45, 0.1] },
      { geometry: new THREE.BoxGeometry(0.1, 0.5, 0.46), color: FIN, position: [0.3, 0.45, 0.1] },
      // Conservator drum.
      { geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.6, 10), color: TANK, position: [-0.1, 0.92, -0.18], rotation: [0, 0, Math.PI / 2] },
    ];
    // Three tall HV bushings rising to a gantry busbar.
    const bx = [-0.28, -0.1, 0.08];
    for (const x of bx) {
      parts.push({ geometry: new THREE.CylinderGeometry(0.03, 0.045, 0.5, 8), color: INSUL, position: [x, 1.05, 0.18] });
      parts.push({ geometry: new THREE.SphereGeometry(0.035, 6, 5), color: TANK_DK, position: [x, 1.32, 0.18] });
    }
    // Gantry posts + busbar over the bushings.
    parts.push(
      { geometry: new THREE.BoxGeometry(0.06, 1.5, 0.06), color: STEEL, position: [-0.45, 0.75, 0.18] },
      { geometry: new THREE.BoxGeometry(0.06, 1.5, 0.06), color: STEEL, position: [0.25, 0.75, 0.18] },
      { geometry: new THREE.BoxGeometry(0.86, 0.06, 0.08), color: STEEL, position: [-0.1, 1.5, 0.18] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.86, 8), color: BUS, position: [-0.1, 1.42, 0.18], rotation: [0, 0, Math.PI / 2] },
    );
    // LV bushings on the near face.
    for (const x of [-0.22, 0.02]) {
      parts.push({ geometry: new THREE.CylinderGeometry(0.025, 0.03, 0.16, 8), color: BUS, position: [x, 0.86, 0.34] });
    }
    return parts;
  },
  animations: [],
});
